import React, { useState, useEffect, useMemo } from 'react';
import { LatLngExpression, LatLngLiteral, LeafletMouseEvent } from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { MapIcon } from './MapIcon';

interface MapProps {
  locationField: {
    label: string;
    value: any;
  };
  handleInputChange: (label: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  maxDistance: number;
  freeDistance: number;
}

interface MapEventHandlerProps {
  position: LatLngLiteral;
  setPosition: (position: LatLngLiteral) => void;
}

const MapEventHandler = ({
  setPosition,
  handleInputChange,
}: MapEventHandlerProps & {
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}) => {
  useMapEvents({
    click: (e: LeafletMouseEvent) => {
      const newPosition = { lat: e.latlng.lat, lng: e.latlng.lng };
      setPosition(newPosition);

      // Call handleInputChange only when user clicks
      const syntheticEvent = {
        target: {
          value: { type: 'Point', coordinates: [newPosition.lng, newPosition.lat] },
        },
      } as unknown as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
      handleInputChange(syntheticEvent);
    },
  });

  return null;
};

export const RestaurantMap = (props: MapProps) => {
  const { locationField, handleInputChange, maxDistance, freeDistance } = props;

  const initialLocation = useMemo(() => {
    const coordinates = locationField?.value?.coordinates;
    if (
      Array.isArray(coordinates) &&
      coordinates.length >= 2 &&
      typeof coordinates[0] === 'number' &&
      typeof coordinates[1] === 'number'
    ) {
      return { lat: coordinates[1], lng: coordinates[0] };
    }
    return { lat: 26.6297, lng: 87.9825 };
  }, [locationField?.value?.coordinates]);

  const [location, setLocation] = useState<LatLngLiteral>(initialLocation);

  useEffect(() => {
    if (location.lat !== initialLocation.lat || location.lng !== initialLocation.lng) {
      setLocation(initialLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation]);

  return (
    <MapContainer center={location} zoom={14} scrollWheelZoom={false} style={{ height: '600px', width: '100%' }}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
      />
      <MapEventHandler
        position={location}
        setPosition={setLocation}
        handleInputChange={handleInputChange('location')}
      />
      <Marker position={location as LatLngExpression} icon={MapIcon}>
        <Popup>Restaurant Location </Popup>
      </Marker>
      <Circle center={location} radius={freeDistance} color="teal" />
      <Circle center={location} radius={maxDistance} color="red" weight={1} />
    </MapContainer>
  );
};
